import { createServerFn } from '@tanstack/react-start';
import { captcha } from '@worldcoo/monolith/captcha';
import {
	getDraftNgos,
	generateContractPdf,
	type ContractData,
	type ContractFiles,
	type ContractLocale,
} from '@worldcoo/monolith/apis/backoffice/server';
import type { Language } from '@worldcoo/monolith/core';
import * as z from 'zod';
import { requireSession, UNAUTHENTICATED, parseIp } from './server-functions-shared.server';
import contractEn from '@worldcoo/monolith/apis/backoffice/documents/Contract-NGO-en.md?raw';
import contractEs from '@worldcoo/monolith/apis/backoffice/documents/Contract-NGO-es.md?raw';
import contractCa from '@worldcoo/monolith/apis/backoffice/documents/Contract-NGO-ca.md?raw';
import contractIt from '@worldcoo/monolith/apis/backoffice/documents/Contract-NGO-it.md?raw';
import signatureTania from '@worldcoo/monolith/apis/backoffice/documents/signature-tania.png?inline';
import signatureRossella from '@worldcoo/monolith/apis/backoffice/documents/signature-rossella.png?inline';

const contractsByLocale: Record<ContractLocale, string> = {
	en: contractEn,
	es: contractEs,
	ca: contractCa,
	it: contractIt,
};

const signaturesByLocale: Record<ContractLocale, string> = {
	it: signatureRossella,
	en: signatureTania,
	ca: signatureTania,
	es: signatureTania,
};

export function getContractFiles(locale: ContractLocale): ContractFiles {
	return {
		contractTemplate: contractsByLocale[locale],
		signatureDataUrl: signaturesByLocale[locale],
	};
}

const contractDataSchema = z.object({
	legalId: z.string(),
	legalName: z.string(),
	ngoName: z.string(),
	legalEmail: z.string(),
	urlProtection: z.string(),
	beneficiariName: z.string(),
	SWIFT: z.string(),
	IBAN: z.string(),
	bankAccountNumber: z.string(),
	ngoTaxId: z.string(),
	ngoAddress: z.string(),
	nowDate: z.string(),
}) satisfies z.ZodType<ContractData>;

/**
 * MARK: Contract PDF generation
 */
export type ContractErrorCode = 'NETWORK_ERROR' | 'CAPTCHA_FAILED' | 'CONTRACT_GENERATION_FAILED';

export const createOnboardingContractFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({ data: contractDataSchema, locale: z.enum(['en', 'es', 'ca', 'it']), captchaToken: z.string() }),
	)
	.handler(async ({ data }) => {
		// Require reCAPTCHA to prevent unauthenticated abuse (Playwright DoS)
		const requestIp = parseIp();
		if (!requestIp) return { error: 'NETWORK_ERROR' as const };
		const captchaResult = await captcha.validate(data.captchaToken, requestIp);
		if (!captchaResult.captchaPassed) {
			return { error: 'CAPTCHA_FAILED' as const };
		}
		try {
			return await generateContractPdf(data.data, getContractFiles(data.locale));
		} catch (err) {
			console.error('Contract generation failed:', err instanceof Error ? err.message : String(err));
			return { error: 'CONTRACT_GENERATION_FAILED' as const };
		}
	});

const languageToContractLocale: Partial<Record<Language, ContractLocale>> = {
	ENG: 'en',
	SPA: 'es',
	CAT: 'ca',
	ITA: 'it',
};

export type DraftContractErrorCode = 'DRAFT_NOT_FOUND' | 'CONTRACT_GENERATION_FAILED';

export const downloadDraftContractFn = createServerFn().handler(async () => {
	const session = requireSession();
	if ('unauthenticated' in session) return UNAUTHENTICATED;
	const { ngoId } = session;
	const [draft] = await getDraftNgos([ngoId]);
	if (!draft) return { error: 'DRAFT_NOT_FOUND' as const };

	const bankInfo = draft.bankInfo as
		| { bankSwiftCode?: string; bankIBANCode?: string; bankAccountNumber?: string }
		| undefined;

	const locale: ContractLocale = languageToContractLocale[draft.language] ?? 'es';

	try {
		return await generateContractPdf(
			{
				legalId: draft.contactLegalId ?? '',
				legalName: `${draft.contactLegalFirstname ?? ''} ${draft.contactLegalLastname ?? ''}`.trim(),
				ngoName: draft.name ?? '',
				legalEmail: draft.contact1Email ?? '',
				urlProtection: draft.privacyUrl ?? '',
				beneficiariName: draft.bankAccountName ?? '',
				SWIFT: bankInfo?.bankSwiftCode ?? '',
				IBAN: bankInfo?.bankIBANCode ?? '',
				bankAccountNumber: bankInfo?.bankAccountNumber ?? '',
				ngoTaxId: draft.taxId ?? '',
				ngoAddress: [draft.address, draft.city, draft.postalCode].filter(Boolean).join(', '),
				nowDate: new Date().toLocaleDateString('es'),
			},
			getContractFiles(locale),
		);
	} catch (err) {
		console.error('Contract generation failed:', err instanceof Error ? err.message : String(err));
		return { error: 'CONTRACT_GENERATION_FAILED' as const };
	}
});
